"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Row, Col, Button, Card, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router";
import { useData } from "../../storage/accessors";
import { useLicense } from "../../hooks/useLicense";
import TrackerCard from "../../components/growth/trackerCard";
import TrackerModal from "../../components/growth/trackerModal";
import Icon from "../../components/Icon";
import { faPlus as add, faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { Tracker } from "../../state/Tracker";
import { LifePart } from "../../state/LifePart";

export default function GrowthPage() {
    const { roles, trackers } = useData();
    const { getLimit } = useLicense();
    const navigate = useNavigate();
    const [selectedPart, setSelectedPart] = useState<LifePart | null>(null);
    const [showTrackerModal, setShowTrackerModal] = useState(false);
    const [editingTracker, setEditingTracker] = useState<Tracker | null>(null);

    const lifeParts = Object.values(roles.objects).sort((a, b) => (a.order || 0) - (b.order || 0));

    const trackersByPart = useCallback((partUuid: string) => {
        return Object.values(trackers.objects)
            .filter((t) => t.lifepart === partUuid)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [trackers.objects]);

    // Фильтруем только те части, у которых есть трекеры
    const lifePartsWithTrackers = useMemo(() => {
        return lifeParts.filter(part => {
            const partTrackers = Object.values(trackers.objects).filter(t => t.lifepart === part.uuid);
            return partTrackers.length > 0;
        });
    }, [lifeParts, trackers.objects]);

    const hasAnyTrackers = Object.values(trackers.objects).length > 0;

    const checkTrackersLimit = (): boolean => {
        const trackersCount = Object.values(trackers.objects).length;
        const trackersLimit = getLimit('limit_trackers');

        if (trackersCount >= trackersLimit) {
            navigate('/my/tariffs?reason=limit_trackers');
            return false;
        }
        return true;
    };

    const handleAddTracker = (partUuid?: string) => {
        if (!checkTrackersLimit()) return;
        setEditingTracker(null);
        if (partUuid) {
            setSelectedPart(lifeParts.find((lp) => lp.uuid === partUuid) || null);
        } else {
            setSelectedPart(null);
        }
        setShowTrackerModal(true);
    };

    const handleEditTracker = (tracker: Tracker) => {
        setEditingTracker(tracker);
        setSelectedPart(lifeParts.find((lp) => lp.uuid === tracker.lifepart) || null);
        setShowTrackerModal(true);
    };

    const handleSaveTracker = (trackerData: Tracker) => {
        if (editingTracker) {
            trackers.save(editingTracker.uuid, trackerData);
        } else {
            trackers.create(trackerData);
        }
    };

    const handleDeleteTracker = (uuid: string) => {
        trackers.delete(uuid);
    };

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h2 className="mb-0">Рост</h2>
                            <p className="text-muted mb-0">Отслеживайте свой прогресс в различных частях жизни</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-info" onClick={() => navigate("./shared")}>
                                <Icon icon={faShareNodes} className="me-2" />
                                Поделиться
                            </Button>
                            {lifeParts.length > 0 && (
                                <Button variant="outline-success" onClick={() => handleAddTracker()}>
                                    <Icon icon={add} className="me-2" />
                                    Добавить трекер
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {lifeParts.length === 0 ? (
                <Alert variant="warning">
                    Сначала создайте части жизни на экране «Баланс» <Link className="btn btn-primary btn-sm" to="./roles">Перейти</Link>
                </Alert>
            ) : (
                <>
                    {!hasAnyTrackers ? (
                        <Card className="mb-4">
                            <Card.Body className="text-center">
                                <h5>Трекеров пока нет</h5>
                                <p className="text-muted mb-3">
                                    Создайте первый трекер для отслеживания прогресса
                                </p>
                                <Button variant="outline-success" onClick={() => handleAddTracker()}>
                                    <Icon icon={add} className="me-2" />
                                    Добавить трекер
                                </Button>
                            </Card.Body>
                        </Card>
                    ) : (
                        <>
                            <div className="mb-3">

                            </div>
                            {lifePartsWithTrackers.map((part) => {
                                const partTrackers = trackersByPart(part.uuid);
                                return (
                                    <Row key={part.uuid} className="mb-4">
                                        <Col>
                                            <Card>
                                                <Card.Header
                                                    className="sticky-top"
                                                    style={{ backgroundColor: part.color, color: "#fff", zIndex: 1000 }}
                                                >
                                                    <h5 className="mb-0">{part.name}</h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Row>
                                                        {partTrackers.map((tracker) => (
                                                            <Col key={tracker.uuid} md={6}>
                                                                <TrackerCard
                                                                    tracker={tracker}
                                                                    lifePart={part}
                                                                    onEdit={() => handleEditTracker(tracker)}
                                                                />
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                );
                            })}
                        </>
                    )}
                </>
            )}

            <TrackerModal
                key={editingTracker?.uuid ?? 'new'}
                show={showTrackerModal}
                onHide={() => setShowTrackerModal(false)}
                onSave={handleSaveTracker}
                onDelete={handleDeleteTracker}
                tracker={editingTracker}
                lifeParts={lifeParts}
                defaultLifePart={selectedPart?.uuid}
            />
        </>
    );
}